import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Diagnostic } from '@ionic-native/diagnostic/ngx';
import { InAppBrowser, InAppBrowserOptions } from '@ionic-native/in-app-browser/ngx';
import { AlertController, LoadingController, Platform } from '@ionic/angular';
import {
  BASE_URL_PATH,
  GROUP_BASE_URL_PATH,
  IMG_200,
  NO_IMG_URL,
  SPICE_KITCHEN_IMG_PROCESS,
  SQUARE_PROCESSOR,
  STONE_CREEK_MERCHANT_ID
} from '../constants';
import { PermissionStatus, Platforms } from '../enums/util.enum';
import { Coordinate, MobileAppMenuTab, RestaurantAddress } from '../models/restaurant.model';
import { RouteService } from './route.service';
import { StorageService } from './storage.service';
import { environment } from '../../../environments/environment';
import { URL } from '../enums/url.enum';
import {Observable, Observer} from 'rxjs';
import { merchantConfig } from 'src/environments/merchant-config';

declare let launchnavigator;
@Injectable({
  providedIn: 'root',
})
export class UtilService {
  public isLoaderActive: boolean = false;
  public isAlertActive: boolean = false;
  private options: InAppBrowserOptions = {
    hidenavigationbuttons: 'yes',
    location: 'no',
  };

  constructor(
    private loadingController: LoadingController,
    private diagnostic: Diagnostic,
    private router: Router,
    private iab: InAppBrowser,
    private alertController: AlertController,
    private routeService: RouteService,
    private storageService: StorageService,
    private platform: Platform,
  ) {}

  public getImageUrl(url: string, imgParam: string = IMG_200, isSpiceKitchen: boolean): string {
    const spiceKitchen = SPICE_KITCHEN_IMG_PROCESS + SQUARE_PROCESSOR + imgParam +  url;
    const umgUrl = isSpiceKitchen ? spiceKitchen : SQUARE_PROCESSOR + imgParam +  url;
    if (environment.apiUrl.includes('sandbox')) {
      return url ? url : NO_IMG_URL;
    }
    return url ? umgUrl : NO_IMG_URL;
  }

  public getDistanceBetweenTwoPoints(cord1: Coordinate, cord2: Coordinate): any {
    if (cord1.latitude == cord2.latitude && cord1.longitude == cord2.longitude) {
      return 0;
    }
    const radlatitude1 = (Math.PI * cord1.latitude) / 180;
    const radlatitude2 = (Math.PI * cord2.latitude) / 180;
    const theta = cord1.longitude - cord2.longitude;
    const radtheta = (Math.PI * theta) / 180;
    let dist = Math.sin(radlatitude1) * Math.sin(radlatitude2) + Math.cos(radlatitude1) * Math.cos(radlatitude2) * Math.cos(radtheta);
    if (dist > 1) {
      dist = 1;
    }
    dist = Math.acos(dist);
    dist = (dist * 180) / Math.PI;
    dist = dist * 60 * 1.1515;
    return dist.toFixed(2);
  }

  public async activeLoader(message: string): Promise<void> {
    this.dismissActiveLoader();
    const loading = await this.loadingController.create({
      cssClass: 'loader',
      message,
      duration: 30000,
    });
    this.isLoaderActive = true;
    await loading.present();
  }

  public async dismissActiveLoader(): Promise<boolean> {
    try {
      if (this.isLoaderActive) {
        this.isLoaderActive = false;
        await this.loadingController.dismiss();
      }
    } catch (err) {
      return false;
    }
  }

  public async presentAlert(header: string, message: string, restaurantClosed = true, merchantId = null, locationId = null): Promise<void> {
    this.isAlertActive = true;
    const alert = await this.alertController.create({
      cssClass: 'my-custom-class',
      header,
      message,
      buttons:
      //  restaurantClosed
      //   ? 
        [
            {
              text: 'Close',
              handler: () => {
                this.dismissAlert();
              },
            },
          ]
        // : [
        //     {
        //       text: 'View Menu',
        //       handler: () => {
        //         this.router.navigate(['merchants', merchantId, 'locations', locationId, 'checkout']);
        //       },
        //     },
        //   ],
    });
    await alert.present();
  }

  public async dismissAlert(): Promise<boolean> {
    try {
      if (this.isAlertActive) {
        this.isAlertActive = false;
        await this.alertController.dismiss();
      }
    } catch (err) {
      return false;
    }
  }

  public isStoneCreek(): boolean {
    return this.routeService.getMerchantId() === STONE_CREEK_MERCHANT_ID;
  }

  public async getLocationAuthState(): Promise<number> {
    try {
      const state = await this.diagnostic.getLocationAuthorizationStatus();
      switch (state) {
        case this.diagnostic.permissionStatus.NOT_REQUESTED:
          return PermissionStatus.GRANTED;
        case this.diagnostic.permissionStatus.DENIED_ALWAYS:
          return PermissionStatus.NOT_REQUESTED;
        case this.diagnostic.permissionStatus.GRANTED:
          return PermissionStatus.DENIED_ALWAYS;
        case this.diagnostic.permissionStatus.GRANTED_WHEN_IN_USE:
          return PermissionStatus.DENIED_ALWAYS;
      }
    } catch (error) {
      return PermissionStatus.DENIED_ALWAYS;
    }
  }

  public async requestLocationAuth(): Promise<boolean> {
    try {
      const permission = await this.diagnostic.requestLocationAuthorization(this.diagnostic.locationAuthorizationMode.WHEN_IN_USE);
      if (permission === this.diagnostic.permissionStatus.GRANTED || permission === this.diagnostic.permissionStatus.GRANTED_WHEN_IN_USE) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  public getCurrentAuthState(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        const permissionStatus = await this.getLocationAuthState();
        if (permissionStatus === PermissionStatus.GRANTED) {
          const isLocationEnabled = await this.isLocationEnabled();
          const response = isLocationEnabled ? await this.requestLocationAuth() : false;
          resolve(response);
        } else {
          resolve(false);
        }
      } catch (error) {
        resolve(true);
      }
    });
  }

  public getCurrentLocation(): Observable<Position> {
    return new Observable((observer: Observer<Position>) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            observer.next(position);
            observer.complete();
          },
          (error) => {
            observer.error(error);
          }
        );
      } else {
        observer.error('Геолокация не поддерживается в вашем браузере');
      }
    });
  }

  private isLocationEnabled(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await this.diagnostic.isLocationEnabled();
        resolve(response);
      } catch (err) {
        resolve(false);
      }
    });
  }

  public openInAppBrowser(url: string): void {
    this.iab.create(url, '_blank', this.options);
  }

  public navigateToContentPage(tab:MobileAppMenuTab, merchantId: string): void {
    this.router.navigate([BASE_URL_PATH, merchantId, 'content-page', tab.title, tab.content])
  }

  public navigateToLocationPage(merchantId: string): void {
    this.router.navigate([BASE_URL_PATH, merchantId]);
  }

  public async navigateToCheckout(): Promise<void> {
    const currentRestaurantId = await this.storageService.get('currentRestaurantId');
    const baseUrl = this.routeService.getBaseUrl();
    await this.router.navigate([baseUrl, 'locations', currentRestaurantId, 'checkout']);
  }

  public navigateToRewards(merchantId:string): void {
    this.router.navigate([BASE_URL_PATH, merchantId, 'rewards']);
  }

  public navigateToGiftCardScan(merchantId: string): void {
    this.router.navigate([BASE_URL_PATH, merchantId, 'gift-card-scan']);
  }

  public navigateToHome(merchantId: string): void {
    this.router.navigate([BASE_URL_PATH, merchantId, 'home-screen']);
  }

  public async navigateToAccount(isGroup?:boolean): Promise<void> {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;

    if (isGroup) {
      const groupId = this.routeService.getGroupId();
      await this.router.navigate([GROUP_BASE_URL_PATH, groupId, 'account']);
    } else {
      const currentRestaurantId = await this.storageService.get('currentRestaurantId');
      const baseUrl = this.routeService.getBaseUrl();
      await this.router.navigate([baseUrl, 'locations', currentRestaurantId, 'account']);
    }
  }

  public async navigateToAccountInfo(): Promise<void> {
    const currentRestaurantId = await this.storageService.get('currentRestaurantId');
    const baseUrl = this.routeService.getBaseUrl();
    await this.router.navigate([baseUrl, 'locations', currentRestaurantId, 'account', 'account-info']);
  }

  public async navigateToPaymentMethods(): Promise<void> {
    const currentRestaurantId = await this.storageService.get('currentRestaurantId');
    const baseUrl = this.routeService.getBaseUrl();
    await this.router.navigate([baseUrl, 'locations', currentRestaurantId, 'account', 'payment-method']);
  }

  public async navigateToPaymentDetail(cardId:string,isGift:boolean=false): Promise<void> {
    const currentRestaurantId = await this.storageService.get('currentRestaurantId');
    const baseUrl = this.routeService.getBaseUrl();
    await this.router.navigate([baseUrl, 'locations', currentRestaurantId, 'account', 'payment-method',cardId]);
  }

  public async navigateToAddPayment(): Promise<void> {
    const currentRestaurantId = await this.storageService.get('currentRestaurantId');
    const baseUrl = this.routeService.getBaseUrl();
    await this.router.navigate([baseUrl, 'locations', currentRestaurantId, 'account', 'payment-method','add']);
  }

  public async navigateToGiftCard(): Promise<void> {
    const currentRestaurantId = await this.storageService.get('currentRestaurantId');
    const baseUrl = this.routeService.getBaseUrl();
    await this.router.navigate([baseUrl, 'locations', currentRestaurantId, 'account', 'gift-card']);
  }

  public async navigateToGiftCardAdd(merchantId: string): Promise<void> {
    await this.router.navigate([BASE_URL_PATH, merchantId, 'gift-card-scan', 'add']);
  }

  public async navigateToGiftCardLoad(cardId: string): Promise<void> {
    const currentRestaurantId = await this.storageService.get('currentRestaurantId');
    const baseUrl = this.routeService.getBaseUrl();
    await this.router.navigate([baseUrl, 'locations', currentRestaurantId, 'account', 'gift-card',cardId]);
  }

  public async navigateToNotifications(): Promise<void> {
    const currentRestaurantId = await this.storageService.get('currentRestaurantId');
    const baseUrl = this.routeService.getBaseUrl();
    await this.router.navigate([baseUrl, 'locations', currentRestaurantId, 'account', 'notifications']);
  }

  public async navigateToSettings(): Promise<void> {
    const currentRestaurantId = await this.storageService.get('currentRestaurantId');
    const baseUrl = this.routeService.getBaseUrl();
    await this.router.navigate([baseUrl, 'locations', currentRestaurantId, 'account', 'settings']);
  }

  public async navigateToNotification(): Promise<void> {
    const currentRestaurantId = await this.storageService.get('currentRestaurantId');
    const baseUrl = this.routeService.getBaseUrl();
    await this.router.navigate([baseUrl, 'locations', currentRestaurantId, 'account', 'notification']);
  }

  public async navigateToSubscriptions(): Promise<void> {
    const currentRestaurantId = await this.storageService.get('currentRestaurantId');
    const baseUrl = this.routeService.getBaseUrl();
    await this.router.navigate([baseUrl, 'locations', currentRestaurantId, 'account', 'subscriptions']);
  }

  public async navigateToSubscription(subscriptionId: string): Promise<void> {
    const currentRestaurantId = await this.storageService.get('currentRestaurantId');
    const baseUrl = this.routeService.getBaseUrl();
    await this.router.navigate([baseUrl, 'locations', currentRestaurantId, 'account', 'subscriptions', subscriptionId]);
  }

  public navigateToSignIn(merchantId: string): void {
    this.router.navigate([BASE_URL_PATH, merchantId, 'sign-in']);
  }

  public openLocationInMaps(restaurantAddress: RestaurantAddress, restaurantName?: string): void {
    const destinationLocation = `${restaurantAddress.addressLine}, ${restaurantAddress.locality}, ${restaurantAddress.postalCode}, ${restaurantAddress.country}`;

    if (!this.platform.is(Platforms.CORDOVA)) {
      // If restaurant name is provided, search for the business name + address to get business listing
      if (restaurantName) {
        const searchQuery = `${restaurantName}, ${destinationLocation}`;
        const newTab = window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`, '_blank');
        newTab.focus();
      } else {
        // Fallback to address only
        const newTab = window.open(`https://www.google.com/maps/place/${destinationLocation}`, '_blank');
        newTab.focus();
      }
    } else {
      launchnavigator.navigate(destinationLocation);
    }
  }
}
